import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { T } from "../../theme";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
`;

/* ════════ LAYOUT ════════ */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${T.bg};
`;

const Center = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px clamp(20px, 5vw, 64px) 80px;
`;

const Glyph = styled.div`
  font-size: clamp(6rem, 16vw, 11rem);
  font-weight: 900;
  letter-spacing: -0.06em;
  line-height: 1;
  background: linear-gradient(
    135deg,
    var(--purple) 0%,
    color-mix(in srgb, var(--purple) 50%, transparent) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  animation: ${float} 4s ease-in-out infinite;
  user-select: none;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(155,81,255,.1);
  border: 1px solid rgba(155,81,255,.22);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--purple);
  margin-bottom: 20px;
  animation: ${fadeUp} 0.35s 0.05s ease both;
`;

const Title = styled.h1`
  font-size: clamp(1.25rem, 3vw, 1.7rem);
  font-weight: 800;
  color: ${T.text};
  margin-bottom: 10px;
  animation: ${fadeUp} 0.35s 0.1s ease both;
`;

const Sub = styled.p`
  font-size: 0.9rem;
  color: ${T.muted};
  max-width: 360px;
  line-height: 1.7;
  margin-bottom: 36px;
  animation: ${fadeUp} 0.35s 0.15s ease both;
`;

const Btns = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeUp} 0.35s 0.2s ease both;
`;

const BtnPrimary = styled.button`
  background: var(--purple);
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 16px rgba(155,81,255,.28);
  transition: filter .14s, transform .12s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
`;

const BtnGhost = styled.button`
  background: transparent;
  border: 1.5px solid ${T.border};
  color: ${T.muted};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color .14s, color .14s;
  &:hover { border-color: var(--purple); color: ${T.text}; }
`;

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
export default function NotFound() {
  const navigate = useNavigate();

  return (
    <Page>
      <Header />
      <Center id="main-content">
        <Glyph aria-hidden="true">404</Glyph>
        <Badge aria-hidden="true"><span>◎</span> Página não encontrada</Badge>
        <Title>Esse endereço não existe.</Title>
        <Sub>
          O link que você tentou acessar foi movido ou nunca existiu.
          Que tal voltar e explorar as vagas abertas?
        </Sub>
        <Btns>
          <BtnPrimary onClick={() => navigate("/")}>Ver vagas abertas</BtnPrimary>
          <BtnGhost onClick={() => navigate("/acompanhar")}>Minha candidatura</BtnGhost>
        </Btns>
      </Center>
      <Footer />
    </Page>
  );
}
